import React from 'react';
import {
  Card,
  Box,
  Typography,
  Avatar,
  Tooltip,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  InsertDriveFile as FileIcon,
  MoreVert as MoreVertIcon,
  CalendarToday as CalendarIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Share as ShareIcon,
  LinkSharp,
  ContentCopy as CopyIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../utils/formatUtils';

const { confirm } = Modal;

/**
 * Form kartı bileşeni
 */
const FormCard = ({
  form,
  onEdit,
  onView,
  onShare,
  onGeneratePublicToken,
  onCopy,
  onArchive,
  onDelete,
  onToggleStatus,
  menuAnchorEl,
  selectedFormForMenu,
  onMenuClick,
  onMenuClose
}) => {
  const { t } = useTranslation();
  const isMenuOpen = Boolean(menuAnchorEl) && selectedFormForMenu?.id === form.id;

  const handleDeleteConfirm = () => {
    confirm({
      title: 'Formu Silmek İstediğinizden Emin misiniz?',
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      okText: 'Evet, Sil',
      cancelText: 'İptal',
      okButtonProps: {
        danger: true,
      },
      onOk: () => {
        onDelete(form.id);
        onMenuClose();
      },
    });
  };

  const handleArchiveConfirm = () => {
    confirm({
      title: 'Formu Arşivlemek İstediğinizden Emin misiniz?',
      icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
      content: 'Form arşive taşınacak ve ana listeden kaldırılacaktır.',
      okText: 'Evet, Arşivle',
      cancelText: 'İptal',
      onOk: () => {
        onArchive(form.id);
        onMenuClose();
      },
    });
  };

  const handleStatusToggleConfirm = () => {
    confirm({
      title: `Formu ${form.isActive ? 'Pasif' : 'Aktif'} Duruma Getirmek İstediğinizden Emin misiniz?`,
      icon: <ExclamationCircleOutlined style={{ color: form.isActive ? '#ff4d4f' : '#52c41a' }} />,
      content: `Form ${form.isActive ? 'pasif' : 'aktif'} duruma getirilecektir.`,
      okText: 'Evet, Değiştir',
      cancelText: 'İptal',
      onOk: () => {
        onToggleStatus(form.id, form.isActive);
      },
    });
  };

  return (
    <>
      <Card className="form-card">
        <Box className="card-header">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar 
              className="header-avatar"
              sx={{ 
                bgcolor: form.isActive ? '#3B82F6' : '#94A3B8'
              }}
            >
              <FileIcon sx={{ fontSize: 18, color: 'white' }} />
            </Avatar>
            <Tooltip title={form.title || 'İsimsiz Form'} placement="top">
              <Typography className="form-title" noWrap>
                {form.title || 'İsimsiz Form'}
              </Typography>
            </Tooltip>
          </Box>
          <Box className="action-buttons">
            <Chip 
              className="status-chip"
              label={form.isActive ? t('active') : t('passive')}
              size="small"
              color={form.isActive ? 'success' : 'error'}
              onClick={handleStatusToggleConfirm}
            />
            <Chip 
              className="response-chip"
              label={`${form.records || 0} ${t('response')}`}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Tooltip title={t('actions')}>
              <IconButton 
                size="small" 
                onClick={(e) => onMenuClick(e, form)}
                className="menu-button"
              >
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box className="card-content">
          <Box className="date-info">
            <Box className="date-item">
              <CalendarIcon className="date-icon" />
              <Typography variant="body2" className="date-text">
                Oluşturulma: {formatDate(form.createDate)}
              </Typography>
            </Box>
            <Box className="date-item">
              <CalendarIcon className="date-icon" />
              <Typography variant="body2" className="date-text">
                Güncelleme: {formatDate(form.editDate)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Card>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={onMenuClose}
        PaperProps={{ className: "menu-paper" }}
      >
        <MenuItem
          component="a"
          href={`/form/${form.formId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">{t('view')}</Typography>
        </MenuItem>
        
        <MenuItem
          component="a"
          href={`/form-edit/${form.formId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">{t('edit')}</Typography>
        </MenuItem>
        
        <MenuItem
          onClick={() => {
            onShare(form.formId);
            onMenuClose();
          }}
        >
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">{t('share')}</Typography>
        </MenuItem>
        
        <MenuItem
          onClick={() => {
            onGeneratePublicToken(form);
            onMenuClose();
          }}
        >
          <ListItemIcon>
            <LinkSharp fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">Önizleme Linki</Typography>
        </MenuItem>
        
        <MenuItem
          onClick={() => {
            onCopy(form);
            onMenuClose();
          }}
        >
          <ListItemIcon>
            <CopyIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">{t('duplicate')}</Typography>
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={handleArchiveConfirm}>
          <ListItemIcon>
            <ArchiveIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">{t('archive')}</Typography>
        </MenuItem>
        
        <MenuItem onClick={handleDeleteConfirm} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <Typography variant="body2">{t('delete')}</Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

export default FormCard;
